import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { reportsService, originationService, portfoliosService } from '../services';

const AppContext = createContext();

const initialState = {
  user: {
    name: 'Administrador',
    email: 'admin@ecpcapital.com',
    role: 'admin'
  },
  kpis: {
    originacao: {
      leads: { total: 0, variacao: 0, meta: 1500 },
      conversao: { taxa: 0, variacao: 0, meta: 10.0 },
      volume: { valor: 0, variacao: 0, meta: 3000000 },
      cac: { valor: 0, variacao: 0, meta: 350 }
    }
  },
  leads: [],
  carteiras: [],
  loading: false,
  error: null,
  filters: {
    leads: {
      status: 'all',
      fonte: 'all',
      periodo: '30d'
    },
    carteiras: {
      tipo: 'all',
      ativo: true
    }
  }
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_DASHBOARD_DATA':
      return {
        ...state,
        kpis: action.payload,
        loading: false
      };
    
    case 'SET_LEADS':
      return {
        ...state,
        leads: action.payload,
        loading: false
      };
    
    case 'SET_PORTFOLIOS':
      return {
        ...state,
        carteiras: action.payload,
        loading: false
      };
    
    case 'UPDATE_LEAD':
      return {
        ...state,
        leads: state.leads.map(lead =>
          lead.id === action.payload.id
            ? { ...lead, ...action.payload.updates }
            : lead
        )
      };
    
    case 'ADD_LEAD':
      return {
        ...state,
        leads: [action.payload, ...state.leads]
      };
    
    case 'DELETE_LEAD':
      return {
        ...state,
        leads: state.leads.filter(lead => lead.id !== action.payload)
      };
    
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.payload.section]: {
            ...state.filters[action.payload.section],
            ...action.payload.filters
          }
        }
      };
    
    case 'UPDATE_KPI':
      return {
        ...state,
        kpis: {
          ...state.kpis,
          [action.payload.section]: {
            ...state.kpis[action.payload.section],
            [action.payload.key]: action.payload.value
          }
        }
      };
    
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Carregar dados da API
  const loadInitialData = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Carregar dados do dashboard
      const dashboardData = await reportsService.getDashboardMetrics();
      dispatch({ type: 'SET_DASHBOARD_DATA', payload: dashboardData });
      
      // Carregar leads
      const leadsData = await originationService.getLeads();
      dispatch({ type: 'SET_LEADS', payload: leadsData });
      
      // Carregar carteiras
      const portfoliosData = await portfoliosService.getPortfolios();
      dispatch({ type: 'SET_PORTFOLIOS', payload: portfoliosData });
      
    } catch (error) {
      // Error loading initial data
      dispatch({ type: 'SET_ERROR', payload: 'Erro ao carregar dados' });
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const actions = {
    setLoading: (loading) => dispatch({ type: 'SET_LOADING', payload: loading }),
    setError: (error) => dispatch({ type: 'SET_ERROR', payload: error }),
    updateLead: (id, updates) => dispatch({ type: 'UPDATE_LEAD', payload: { id, updates } }),
    addLead: (lead) => dispatch({ type: 'ADD_LEAD', payload: lead }),
    deleteLead: (id) => dispatch({ type: 'DELETE_LEAD', payload: id }),
    updateFilters: (section, filters) => dispatch({ type: 'UPDATE_FILTERS', payload: { section, filters } }),
    updateKPI: (section, key, value) => dispatch({ type: 'UPDATE_KPI', payload: { section, key, value } }),
    refreshData: loadInitialData
  };

  return (
    <AppContext.Provider value={{ ...state, ...actions }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}